// src/api/services/customer.ts
import { api } from './index';

// Customer interfaces
export interface Customer {
  id: number;
  name: string;
  telephone?: string;
  address?: string;
}

export interface CustomerCreateDTO {
  name: string;
  telephone?: string;
  address?: string;
}

export interface CustomerUpdateDTO {
  name?: string;
  telephone?: string;
  address?: string;
}

// Query options interface
export interface QueryOptions {
  max?: number;
  offset?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  view?: string;
  additional?: string;
  [key: string]: any; // For custom filters
}

// Where query interface
export interface WhereQuery {
  where: string;
  params: Record<string, any>;
}

// Response interfaces
export interface ListResponse<T> {
  statusCode: number;
  data: T[];
  totalCount: number;
}

export interface SingleResponse<T> {
  statusCode: number;
  data: T;
}

// Customer service
export const customerService = {
  /**
   * List all customers with optional pagination and sorting
   */
  list: (options?: QueryOptions) =>
    api.get<ListResponse<Customer>>('/customer', { params: options }),

  /**
   * Find customers with complex filtering
   */
  where: (whereData: WhereQuery, options?: QueryOptions) =>
    api.post<ListResponse<Customer>>('/customer/where', whereData, { 
      params: options 
    }),

  /**
   * Get a single customer by ID
   */
  read: (id: number, options?: Pick<QueryOptions, 'view' | 'additional'>) =>
    api.get<SingleResponse<Customer>>(`/customer/${id}`, { 
      params: options 
    }),

  /**
   * Create a new customer
   */
  create: (data: CustomerCreateDTO) =>
    api.post<SingleResponse<Customer>>('/customer', data),

  /**
   * Update an existing customer
   */
  update: (id: number, data: CustomerUpdateDTO) =>
    api.put<SingleResponse<Customer>>(`/customer/${id}`, data),

  /**
   * Delete a customer
   */
  delete: (id: number) =>
    api.delete<{ statusCode: number }>(`/customer/${id}`),

  listAll: async () => {
    let allCustomers: Customer[] = [];
    let offset = 0;
    const max = 99; // Maximum limit per request

    while (true) {
      const response = await customerService.list({ max, offset });
      allCustomers = allCustomers.concat(response.data.data);

      if (response.data.data.length < max) {
        break; // Exit loop if fewer items than max were returned
      }

      offset += max; // Increment offset for the next batch
    }

    return allCustomers;
  },
  
  /**
   * Example of using the common filter patterns
   * This is a helper method to demonstrate usage
   */
  findByName: (name: string, options?: QueryOptions) => {
    const whereData: WhereQuery = {
      where: "_.name LIKE :name",
      params: { name: `%${name}%` }
    };
    return customerService.where(whereData, options);
  }
};